/**
 * NovaAudioRecorder
 * Handles audio recording for Nova Sonic with proper format
 * Records at 16kHz, mono, 16-bit PCM and converts to base64
 */

export default class NovaAudioRecorder {
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private mediaStreamSource: MediaStreamAudioSourceNode | null = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  private isRecording: boolean = false;
  private onAudioDataCallback: ((base64Audio: string) => void) | null = null;
  private targetSampleRate: number = 16000; // Nova Sonic input rate
  // Bumped by stop(): a start() still waiting for the mic sees it and lets go.
  private generation: number = 0;

  /**
   * Start recording audio
   */
  async start(onAudioData: (base64Audio: string) => void): Promise<void> {
    const generation = this.generation;
    try {
      // Request microphone access
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      if (generation !== this.generation) {
        // stop() ran while the mic was being opened: release it, start nothing.
        mediaStream.getTracks().forEach((track) => track.stop());
        return;
      }
      this.mediaStream = mediaStream;

      // Create audio context with default sample rate (matches mic's native rate)
      // Firefox doesn't allow connecting AudioNodes from AudioContexts with different sample rates
      // So we use the default rate and resample manually to 16kHz
      this.audioContext = new AudioContext();
      this.mediaStreamSource = this.audioContext.createMediaStreamSource(this.mediaStream);

      const sourceSampleRate = this.audioContext.sampleRate;
      console.log(`Audio context sample rate: ${sourceSampleRate}, target: ${this.targetSampleRate}`);

      // Create script processor for audio processing
      // Note: ScriptProcessor is deprecated but still widely supported
      // For production, consider using AudioWorklet
      this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);

      this.onAudioDataCallback = onAudioData;

      // Process audio data
      this.scriptProcessor.onaudioprocess = (event: AudioProcessingEvent) => {
        if (!this.isRecording) return;

        const inputData = event.inputBuffer.getChannelData(0);

        // Resample to 16kHz if needed
        const resampledData = this.resampleAudio(inputData, sourceSampleRate, this.targetSampleRate);

        // Convert Float32Array to Int16Array (16-bit PCM)
        const pcmData = this.float32ToInt16(resampledData);

        // Convert to base64
        const base64Audio = this.arrayBufferToBase64(pcmData.buffer);

        // Send to callback
        if (this.onAudioDataCallback) {
          this.onAudioDataCallback(base64Audio);
        }
      };

      // Connect the audio graph
      this.mediaStreamSource.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);

      this.isRecording = true;
      console.log('Audio recording started');
    } catch (error) {
      console.error('Failed to start audio recording:', error);
      throw error;
    }
  }

  /**
   * Stop recording audio
   */
  stop(): void {
    this.generation += 1;
    this.isRecording = false;

    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }

    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.onAudioDataCallback = null;
    console.log('Audio recording stopped');
  }

  /**
   * Resample audio data from source sample rate to target sample rate
   * Uses linear interpolation for simplicity
   */
  private resampleAudio(inputData: Float32Array, sourceSampleRate: number, targetSampleRate: number): Float32Array {
    if (sourceSampleRate === targetSampleRate) {
      return inputData;
    }

    const ratio = sourceSampleRate / targetSampleRate;
    const newLength = Math.round(inputData.length / ratio);
    const result = new Float32Array(newLength);

    for (let i = 0; i < newLength; i++) {
      const srcIndex = i * ratio;
      const srcIndexFloor = Math.floor(srcIndex);
      const srcIndexCeil = Math.min(srcIndexFloor + 1, inputData.length - 1);
      const fraction = srcIndex - srcIndexFloor;

      // Linear interpolation
      result[i] = inputData[srcIndexFloor] * (1 - fraction) + inputData[srcIndexCeil] * fraction;
    }

    return result;
  }

  /**
   * Convert Float32Array to Int16Array (16-bit PCM)
   */
  private float32ToInt16(float32Array: Float32Array): Int16Array {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      // Clamp to [-1, 1] and convert to 16-bit integer
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return int16Array;
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBufferLike): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Check if recording is active
   */
  isActive(): boolean {
    return this.isRecording;
  }
}
